import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';

const SESSION_WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes total session time
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

export const useSessionManager = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetActivity = () => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  };

  const checkSession = () => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;

    // Show warning if approaching timeout
    if (timeSinceActivity > SESSION_WARNING_TIME && !warningShownRef.current) {
      warningShownRef.current = true;
      console.warn('Session will expire soon due to inactivity');
      // You could show a toast notification here
    }

    // Logout if session has expired
    if (timeSinceActivity > SESSION_TIMEOUT) {
      console.warn('Session expired due to inactivity');
      dispatch(logout());
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    // Set up activity listeners
    const handleActivity = () => resetActivity();
    
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Check session every minute
    timeoutRef.current = setInterval(checkSession, 60000);

    return () => {
      // Cleanup
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (timeoutRef.current !== null) {
        clearInterval(timeoutRef.current);
      }
    };
  }, [isAuthenticated, dispatch]);

  return {
    resetActivity,
    lastActivity: lastActivityRef.current
  };
};